import * as React from "react";
import {
    Box,
    Button,
    Flashbar,
    Link,
    Modal,
    Popover,
    ProgressBar,
    Select,
    SpaceBetween,
    Toggle
} from "@amzn/awsui-components-react";
import { AppContext } from "../../setup/context";
import {
    APIOutput,
    ContainerRef,
    EntityRef,
    EntityType,
    IContainer,
    Instance,
    InstanceFactory,
    IPage,
    Page,
    PageFactory,
    PageLibrary,
    PageLibraryInterface,
    ServerSidePaginatedLoadingInput
} from "@amzn/ask-legal-domain";
import { UIField } from "../common/UIField";
import { UIModel } from "../../model/ui-model";
import { PageLibraryModel } from "../../model/page-library-model";
import { ErrorFlashbar } from "../common/ErrorFlashbar";
import { useNavigate } from "react-router-dom";

interface CopyResult {
    pageId: string;
    pageName: string;
    success: boolean;
    error?: string;
    partial?: boolean;
    failedContainers?: string[];
}

type ModalStatus = "Config" | "Running" | "Done";

export const CopyPageLibraryModal = (props: {
    onDismiss: () => void;
    sourcePageLibrary: PageLibrary;
}) => {
    const navigate = useNavigate();
    const context = React.useContext(AppContext);
    const sourceInstanceId = InstanceFactory.fromEntityId(props.sourcePageLibrary.id);

    // Instance selection
    const [selectedInstance, setSelectedInstance] = React.useState<EntityRef | null>({ id: sourceInstanceId } as EntityRef);
    const [instanceOptions, setInstanceOptions] = React.useState([
        { label: sourceInstanceId, value: sourceInstanceId }
    ]);
    const [instancesLoaded, setInstancesLoaded] = React.useState(false);
    const [instanceLoadError, setInstanceLoadError] = React.useState(false);

    // Page library name
    const newPLName = UIModel.State.useWithRegexAndCharLimit({
        initialValue: "",
        regex: new RegExp(/^[a-zA-Z0-9]+$/),
        characterLimit: PageLibraryModel.TITLE_CHAR_LIMIT
    });
    const newPLDisplayName = UIModel.State.useWithRegexAndCharLimit({
        initialValue: props.sourcePageLibrary.name || "",
        regex: new RegExp(/^.+$/),
        characterLimit: PageLibraryModel.TITLE_CHAR_LIMIT
    });

    // Copy settings
    const [deprecateSourcePages, setDeprecateSourcePages] = React.useState(false);

    // Progress state
    const [status, setStatus] = React.useState<ModalStatus>("Config");
    const [totalPages, setTotalPages] = React.useState(0);
    const [completedPages, setCompletedPages] = React.useState(0);
    const [results, setResults] = React.useState<CopyResult[]>([]);
    const [createdPLId, setCreatedPLId] = React.useState<string | null>(null);
    const [globalError, setGlobalError] = React.useState<string | null>(null);

    // Load instances
    React.useEffect(() => {
        let cancelled = false;
        context.getInstanceAPI().loadAll().then(res => {
            if (cancelled) return;
            const output = APIOutput.fromRaw<Instance[]>(res.data);
            if (output.isOk()) {
                setInstanceOptions(output.data.map(inst => ({
                    label: inst.name || inst.id,
                    value: inst.id
                })).sort((a, b) => a.label.localeCompare(b.label)));
            } else {
                setInstanceLoadError(true);
            }
            setInstancesLoaded(true);
        }).catch(() => {
            if (cancelled) return;
            setInstanceLoadError(true);
            setInstancesLoaded(true);
        });
        return () => { cancelled = true; };
    }, []);

    const loadAllPages = async (): Promise<Page.Data[]> => {
        const allPages: Page.Data[] = [];
        let currentPage = 1;
        const PAGE_LOAD_BATCH_SIZE = 100;
        while (true) {
            const res = await context.getPageAPI().loadAll(
                ServerSidePaginatedLoadingInput.create({
                    partitionKey: props.sourcePageLibrary.id,
                    currentPageIndex: currentPage,
                    pageSize: PAGE_LOAD_BATCH_SIZE,
                    filters: []
                })
            );
            const output = APIOutput.fromRaw<{ result: Page.Data[]; totalCount: number }>(res.data);
            if (output.isErr()) throw new Error(output.err.message);
            const batch = output.data.result;
            if (batch.length === 0) break;
            allPages.push(...batch);
            if (allPages.length >= output.data.totalCount) break;
            currentPage++;
        }
        return allPages;
    };

    const startCopy = async () => {
        setStatus("Running");
        setGlobalError(null);
        let localCreatedPLId: string | null = null;

        try {
            // 1. Create the target page library
            const targetInstanceId = selectedInstance.id;
            const createPLRes = await context.getPageLibraryAPI().create(
                PageLibraryInterface.CreatePageLibraryInput.create(
                    { id: targetInstanceId, type: EntityType.Instance } as EntityRef,
                    newPLName.value,
                    newPLDisplayName.value,
                    props.sourcePageLibrary.freshness,
                    props.sourcePageLibrary.restrictToLegal,
                    props.sourcePageLibrary.shared || false,
                    props.sourcePageLibrary.enableApprovalWorkflow || false
                )
            );
            const createPLOutput = APIOutput.fromRaw<PageLibrary>(createPLRes.data);
            if (createPLOutput.isErr()) {
                setGlobalError(`Failed to create page library: ${createPLOutput.err.message}`);
                setStatus("Done");
                return;
            }
            const targetPLId = createPLOutput.data.id;
            localCreatedPLId = targetPLId;
            setCreatedPLId(targetPLId);

            // 2. Load all pages from source
            const pages = await loadAllPages();
            const nonDeprecatedPages = pages.filter(p => !p.deprecated);
            setTotalPages(nonDeprecatedPages.length);

            if (nonDeprecatedPages.length === 0) {
                setStatus("Done");
                return;
            }

            // 3. Copy pages one at a time using multi-call flow
            const isCrossInstance = targetInstanceId !== sourceInstanceId;
            const copyResults: CopyResult[] = [];

            for (const page of nonDeprecatedPages) {
                const pageName = PageFactory.getNameFromPageId(page.id);
                try {
                    // Step 1: Init
                    const initInput = IPage.DeepCopyPageInitInput.create({
                        sourcePageId: page.id,
                        targetPageId: PageFactory.toPageId(targetPLId, pageName),
                        deprecateSourcePage: deprecateSourcePages,
                        ...(isCrossInstance && {
                            targetInstanceId,
                            targetPageLibraryId: targetPLId,
                            newPageName: pageName,
                        }),
                    });
                    const initRes = await context.getPageAPI().deepCopyInit(initInput);
                    const initOutput = APIOutput.fromRaw<IPage.DeepCopyPageInitOutput>(initRes.data);
                    if (initOutput.isErr()) {
                        copyResults.push({ pageId: page.id, pageName, success: false, error: initOutput.err.message });
                        setCompletedPages(prev => prev + 1);
                        continue;
                    }

                    const { targetPageId, containers } = initOutput.data;

                    // Step 2: Copy each container
                    const mainContainers: ContainerRef[] = [];
                    const rightContainers: ContainerRef[] = [];
                    const failedContainerTypes: string[] = [];

                    for (const container of containers) {
                        const copyRes = await context.getContainerAPI().deepCopy({
                            containerId: container.containerId,
                            targetPageId,
                        });
                        const copyOutput = APIOutput.fromRaw<IContainer.DeepCopyContainerOutput>(copyRes.data);
                        if (copyOutput.isErr()) {
                            if (copyOutput.err.code === 422) continue; // Skip unsupported
                            failedContainerTypes.push(container.containerType);
                            continue;
                        }
                        if (container.section === "Main") {
                            mainContainers.push(copyOutput.data.newContainer);
                        } else {
                            rightContainers.push(copyOutput.data.newContainer);
                        }
                    }

                    // If all containers failed, mark page as failed and skip finalize
                    if (mainContainers.length === 0 && rightContainers.length === 0) {
                        copyResults.push({ pageId: page.id, pageName, success: false, error: `All containers failed to copy` });
                        setCompletedPages(prev => prev + 1);
                        continue;
                    }

                    // Step 3: Finalize with whatever containers succeeded
                    const finalizeInput = IPage.DeepCopyPageFinalizeInput.create({
                        targetPageId,
                        sourcePageId: page.id,
                        deprecateSourcePage: deprecateSourcePages,
                        mainContainers,
                        rightContainers,
                    });
                    const finalizeRes = await context.getPageAPI().deepCopyFinalize(finalizeInput);
                    const finalizeOutput = APIOutput.fromRaw<IPage.DeepCopyPageFinalizeOutput>(finalizeRes.data);
                    if (finalizeOutput.isErr()) {
                        copyResults.push({ pageId: page.id, pageName, success: false, error: finalizeOutput.err.message });
                    } else if (failedContainerTypes.length > 0) {
                        copyResults.push({ pageId: page.id, pageName, success: true, partial: true, failedContainers: failedContainerTypes });
                    } else {
                        copyResults.push({ pageId: page.id, pageName, success: true });
                    }
                } catch (e: any) {
                    copyResults.push({
                        pageId: page.id,
                        pageName,
                        success: false,
                        error: e?.response?.data?.message || e?.message || "Unknown error"
                    });
                }
                setCompletedPages(prev => prev + 1);
            }
            setResults(copyResults);
        } catch (e: any) {
            const msg = e?.message || "Unexpected error during copy";
            setGlobalError(
                localCreatedPLId
                    ? `Page library was created but copying pages failed: ${msg}. You can navigate to the new library and retry, or delete it.`
                    : msg
            );
        }
        setStatus("Done");
    };

    const successCount = results.filter(r => r.success).length;
    const failedResults = results.filter(r => !r.success);
    const partialResults = results.filter(r => r.partial);
    const progressValue = totalPages > 0 ? Math.round((completedPages / totalPages) * 100) : 0;

    return (
        <Modal
            visible={true}
            size="medium"
            header="Copy Page Library"
            onDismiss={status === "Running" ? undefined : props.onDismiss}
            footer={
                <Box float="right">
                    {status === "Config" && <>
                        <Button variant="link" onClick={props.onDismiss}>Cancel</Button>
                        <Button
                            variant="primary"
                            disabled={
                                !selectedInstance?.id ||
                                !newPLName.value || newPLName.errorText ||
                                !newPLDisplayName.value || newPLDisplayName.errorText
                            }
                            onClick={startCopy}
                        >
                            Start Copy
                        </Button>
                    </>}
                    {status === "Running" && <Button variant="link" disabled>Copying...</Button>}
                    {status === "Done" && <>
                        <Button variant="link" onClick={props.onDismiss}>Close</Button>
                        {createdPLId && <Button
                            variant="primary"
                            onClick={() => navigate(`/page-library/${createdPLId}/admin`)}
                        >
                            Go to new Page Library
                        </Button>}
                    </>}
                </Box>
            }
        >
            {status === "Config" && (
                <SpaceBetween size="xs">
                    {instanceLoadError && (
                        <Flashbar items={[{
                            type: "warning",
                            content: "Failed to load full instance list. Only the source instance is available.",
                            dismissible: false,
                            id: "instance-load-error"
                        }]} />
                    )}
                    <UIField.CustomField
                        name="Target instance:"
                        child={<Select
                            selectedOption={instanceOptions.find(o => o.value === selectedInstance?.id) || null}
                            options={instanceOptions}
                            onChange={({ detail }) => setSelectedInstance({ id: detail.selectedOption.value } as EntityRef)}
                            statusType={instancesLoaded ? "finished" : "loading"}
                            loadingText="Loading instances..."
                            filteringType="auto"
                            placeholder="Select instance"
                        />}
                    />
                    <UIField.StateValueField
                        name="New Page Library Name (part of the URL)"
                        state={newPLName}
                        constraintText={
                            <React.Fragment>
                                Maximum {PageLibraryModel.TITLE_CHAR_LIMIT} characters <Popover
                                    dismissAriaLabel="Close"
                                    header="Naming Guidelines"
                                    triggerType="custom"
                                    content={<small><ul>
                                        <li>Cannot contain spaces or special characters</li>
                                        <li>Alphabets A-Z, a-z and numbers 0-9 only</li>
                                    </ul></small>}
                                ><Link variant="info">Format</Link></Popover>
                            </React.Fragment>}
                        editing={true}
                    />
                    <UIField.StateValueField
                        name="Display Name"
                        state={newPLDisplayName}
                        constraintText={`Maximum ${PageLibraryModel.TITLE_CHAR_LIMIT} characters`}
                        editing={true}
                    />
                    <Toggle
                        checked={deprecateSourcePages}
                        onChange={e => setDeprecateSourcePages(e.detail.checked)}
                    >
                        Deprecate source pages after copy
                    </Toggle>
                    {deprecateSourcePages && (
                        <Flashbar items={[{
                            type: "warning",
                            content: "This will deprecate all source pages after copying. This action cannot be easily undone.",
                            dismissible: false,
                            id: "deprecate-warning"
                        }]} />
                    )}
                </SpaceBetween>
            )}

            {status === "Running" && (
                <SpaceBetween size="m">
                    <ProgressBar
                        value={progressValue}
                        additionalInfo="Please do not refresh the page or close the tab."
                        description={totalPages > 0
                            ? `${completedPages} of ${totalPages} pages copied`
                            : "Creating page library..."
                        }
                        label="Copying page library"
                    />
                </SpaceBetween>
            )}

            {status === "Done" && (
                <SpaceBetween size="m">
                    {globalError && <ErrorFlashbar error={{ message: globalError, code: 500 }} />}
                    {results.length > 0 && (
                        <Flashbar items={[
                            ...(successCount > 0 ? [{
                                type: "success" as const,
                                content: `Successfully copied ${successCount} of ${totalPages} page${totalPages !== 1 ? "s" : ""}.`,
                                dismissible: false,
                                id: "success"
                            }] : []),
                            ...partialResults.map((r, i) => ({
                                type: "warning" as const,
                                content: `Page "${r.pageName}" copied partially — failed containers: ${r.failedContainers!.join(", ")}`,
                                dismissible: false,
                                id: `partial-${i}`
                            })),
                            ...failedResults.map((r, i) => ({
                                type: "error" as const,
                                content: `Failed to copy page "${r.pageName}": ${r.error}`,
                                dismissible: false,
                                id: `error-${i}`
                            }))
                        ]} />
                    )}
                    {!globalError && totalPages === 0 && createdPLId && (
                        <Flashbar items={[{
                            type: "success" as const,
                            content: "Page library created. No non-deprecated pages to copy.",
                            dismissible: false,
                            id: "empty"
                        }]} />
                    )}
                </SpaceBetween>
            )}
        </Modal>
    );
};
